import {loadLink} from "../click-menu/helpers";
import {EntListingActionLogic} from "../../classes/list/ent-listing-action-logic";
import {setupAjax} from "../../common/ajax";
import {ef_installFormChangeListenerForForm} from "../../classes/form/easy-forms";
import {CA_enableButton} from "../../common/dom";
import {CA_showHelp, openLinkInNewWindow} from "../../common/nav";
import {removeQueryParam, setQueryParam} from "../../common/url";

/**
 * Loads listPage specific logic.
 *
 * List Pages expect the following tags to be found on the page:
 * 	- <csp:listingHeader/>
 * 	- <csp:sidebar/>
 * 	- <csp:listingFooter/>
 * Other tags are needed for List Pages, but the ones above require additional JS logic
 * to be applied.
 *
 */
export function loadListPage() {
    if (!GlobalEnt.pageData) {
        console.error("Page Data has not loaded...");
        return;
    }
    setupListPageLogic();
    loadListingHeader();
    loadListingFooter();
    loadPage();
}

function setupListPageLogic() {
    const { pageData } = GlobalEnt;
    const { nSelected, totalNumItems, nSelectable,
        persistentSelectionAttr, autoScrollRowID,
        listingState, csrfToken, selectedOnPage,
        clipboardContentHash, clipboardItemCount,
        clipboardOperation } = pageData;

    // Strip single quotes and generate comma-delimited string into an array.
    let selectedOnPageArr = [];
    if (selectedOnPage) {
        selectedOnPageArr = selectedOnPage.replace(/["']/g, "").split(',');
    }

    // Loads global logic object used to interact with the List Page.
    GlobalEnt.listPageLogic = new EntListingActionLogic(
        nSelected,
        totalNumItems,
        nSelectable,
        selectedOnPageArr,
        clipboardContentHash,
        persistentSelectionAttr,
        autoScrollRowID,
        listingState,
        csrfToken,
        clipboardItemCount,
        clipboardOperation
    );
}

function loadListingHeader() {
    // Elements can be optional.
    const listingHeaderDetails = document.getElementById("listing-header-details");
    const listingHeaderAdd = document.getElementById("listing-header-add");
    const listingHeaderDelete = document.getElementById("listing-header-delete");
    const listingHeaderHide = document.getElementById("listing-header-hide");
    const listingHeaderCut = document.getElementById("listing-header-cut");
    const listingHeaderCopy = document.getElementById("listing-header-copy");
    const listingHeaderPaste = document.getElementById("listing-header-paste");
    const listingHeaderSearch = document.getElementById("listing-header-search");
    const filterAppliedAction = document.getElementById("filterAppliedAction");
    const itemsCopiedElm = document.querySelector('.items-copied-label');

    if (listingHeaderDetails) {
        listingHeaderDetails.addEventListener('click', function() {
            const slideshow = document.querySelector('.slideshow-container');
            if (slideshow) {
                if (slideshow.classList.contains('hidden')) {
                    slideshow.classList.remove('hidden');
                } else {
                    slideshow.classList.add('hidden');
                }
            }
        });
    }

    const { listPageLogic } = GlobalEnt;

    listPageLogic.refreshCountText();

    // The ADD action should only need to load the link because it doesn't have unique
    // ListPageLogic procedures.
    if (listingHeaderAdd) {
        const { action } = listingHeaderAdd.dataset;
        listingHeaderAdd.addEventListener('click', function() {
            loadLink(action);
        });
    }

    if (listingHeaderDelete) {
        const { action } = listingHeaderDelete.dataset;
        listingHeaderDelete.addEventListener("click", function() {
            if (!action.includes('@DEFAULT')) {
                listPageLogic.params.deleteAction = action;
            }
            listPageLogic.doDelete();
        });
    }

    if (listingHeaderHide) {
        const { action } = listingHeaderHide.dataset;
        listingHeaderHide.addEventListener("click", function() {
            if (!action.includes('@DEFAULT')) {
                listPageLogic.params.hideAction = action;
            }
            listPageLogic.doHide();
        });
    }

    // Custom overrides aren't applicable for the CUT action because it simply
    // posts an AjaxRequest to the server to save entry info to paste in another subsystem.
    if (listingHeaderCut) {
        listingHeaderCut.addEventListener("click", function() {
            listPageLogic.doCut();
        });
    }

    // Custom overrides aren't applicable for the COPY action because it simply
    // posts an AjaxRequest to the server to save entry info to paste in another subsystem.
    if (listingHeaderCopy) {
        listingHeaderCopy.addEventListener("click", function() {
            listPageLogic.doCopy();
        });
    }

    if (listingHeaderPaste) {
        const { action } = listingHeaderPaste.dataset;
        listingHeaderPaste.addEventListener("click", function() {
            if (!action.includes('@DEFAULT')) {
                listPageLogic.params.pasteAction = action;
            }
            listPageLogic.doPaste();
        });
    }

    if (listingHeaderSearch) {
        listingHeaderSearch.addEventListener("click", function() {
            document.querySelector('.content-container').classList.remove('tree-collapsed');
            document.querySelector('.filter-input input').focus();
        });
    }

    if (filterAppliedAction) {
        filterAppliedAction.addEventListener('click', function () {
            document.getElementById('clearFilterBtn').click();
        });
    }

    if (itemsCopiedElm) {
        itemsCopiedElm.addEventListener('click', function() {
            listPageLogic.clearSelection();
        });
    }
}

/**
 * Handles listing table behavior, such as which page to show, how many rows per page, etc.
 */
function loadListingFooter() {
    const { pageData, listPageLogic } = GlobalEnt;
    const { params } = pageData;
    const { pgIdx: pgIdxVal, pgSz: pgSzVal } = params;
    const useListPageLogic = isNaN(pgIdxVal) || isNaN(pgSzVal);

    const pgIdx = parseInt(pgIdxVal);
    const pgSz = parseInt(pgSzVal);

    const nextAction = document.getElementById("nextAction");
    if (nextAction) {
        nextAction.addEventListener('click', function () {
            if (useListPageLogic)
                listPageLogic.pager.goToNext();
            else
                nextAction.href = "?pgIdx=" + (pgIdx + pgSz);
        });
    }

    const prevAction = document.getElementById("prevAction");
    if (prevAction) {
        prevAction.addEventListener('click', function () {
            if (useListPageLogic)
                listPageLogic.pager.goToPrev();
            else
                prevAction.href = "?pgIdx=" + (pgIdx - pgSz);
        });
    }

    const firstAction = document.getElementById("firstAction");
    if (firstAction) {
        firstAction.addEventListener('click', function () {
            if (useListPageLogic)
                listPageLogic.pager.goToFirst();
            else
                firstAction.href = "?pgIdx=0";
        });
    }

    const lastAction = document.getElementById("lastAction");
    if (lastAction) {
        lastAction.addEventListener('click', function () {
            if (useListPageLogic)
                listPageLogic.pager.goToLast();
            else
                lastAction.href = "?pgIdx=99999999";
        });
    }

    const pageForm = document.getElementById("PageForm");
    const pgListSize = document.getElementById("pgListSize");
    if (pgListSize && pageForm) {
        pgListSize.addEventListener('change', function() {
            pageForm.submit();
        });
    }
}

/**
 * Loads misc logic to the List Page.
 */
function loadPage() {
    CA_enableButton("list-button-add", true);

    const { pageData, listPageLogic } = GlobalEnt;
    const { _initialAjaxState } = pageData;

    // References a script file not included in ent/ module bundle.
    if (_initialAjaxState)
        setupAjax(_initialAjaxState);

    if (document.filterForm) {
        ef_installFormChangeListenerForForm(document.filterForm, function() {
            if (!document.filterForm.applyFilterBtn) {
                console.error("Failed to install form change listener to filterForm...");
                return;
            }
            document.filterForm.applyFilterBtn.disabled = false;
        });
    }

    const chkAll = document.getElementById('chkAll');
    if (chkAll) {
        chkAll.addEventListener('click', function () {
            listPageLogic.onAllToggle();
        });
    }

    const rows = document.querySelectorAll(".clickableRow");
    if (rows) {
        rows.forEach((clickRow) => {
            const actionEl = clickRow.querySelector('.show-more-item');
            if (!actionEl) return;

            const { id } = clickRow.dataset;
            actionEl.addEventListener('click', (e) => {
                e.stopPropagation();

                const contentEl = document.querySelector(`.click-row-content-${id}`);
                const iconEl = actionEl.querySelector('.show-more-item-icon');
                if (!contentEl || !iconEl) return;

                if (contentEl.classList.contains('show-more-hidden')) {
                    contentEl.classList.remove('show-more-hidden');
                    contentEl.classList.add('show-more-displayed');
                    iconEl.src = WebpackConfig.icons.chevronUp;
                    createAndDisplayContentTable(contentEl, clickRow);
                } else if (contentEl.classList.contains('show-more-displayed')) {
                    contentEl.classList.remove('show-more-displayed');
                    contentEl.classList.add('show-more-hidden');
                    iconEl.src = WebpackConfig.icons.chevronDown;
                    removeContentTable(contentEl);
                }
            });
        });
    }

    const listingHeaderLinks = document.querySelectorAll('.listing-header-link');
    if (listingHeaderLinks) {
        listingHeaderLinks.forEach((listingHeaderLink) => {
            const { link, openInNewWindow, openInNewTab } = listingHeaderLink.dataset;
            if (link) {
                listingHeaderLink.addEventListener('click', () => {
                    if (openInNewWindow) {
                        openLinkInNewWindow(link, true);
                    } else if (openInNewTab) {
                        window.open(link, '_blank').focus();
                    } else {
                        window.location = link;
                    }
                })
            }
        });
    }
}

function createAndDisplayContentTable(contentEl, clickRow) {
    if (!contentEl) return;
    const innerContentEl = contentEl.querySelector('td');
    if (!innerContentEl) return;
    innerContentEl.appendChild(getContentTableEl(clickRow));
}

function getContentTableEl(clickRow) {
    const tableEl = document.createElement('table');
    tableEl.classList.add('row-content-table');
    appendContentTableHeader(tableEl);
    appendContentTableRows(tableEl, clickRow);
    return tableEl;
}

function appendContentTableHeader(tableEl) {
    const headerCells = [
        createTableCell(i18n.common.key, 'content-table-header'),
        createTableCell(i18n.common.value, 'content-table-header')
    ];
    tableEl.appendChild(createTableHead(createTableRow(headerCells)));
}

function appendContentTableRows(tableEl, clickRow) {
    const listingHeaderRow = document.querySelector('.listing-header-row');
    if (!listingHeaderRow) return;

    const columnHeaders = listingHeaderRow.querySelectorAll('.list-header-content-col');
    const listRowTds = clickRow.querySelectorAll('.list-row-content-td');

    const keys = [];
    for (let i = 0; i < columnHeaders.length; i++) {
        const keyDiv = document.createElement('div');
        columnHeaders[i].childNodes.forEach(function(item) {
            keyDiv.appendChild(item.cloneNode(true));
        });
        keys.push(keyDiv);
    }

    const values = [];
    for (let i = 0; i < listRowTds.length; i++) {
        const rowDiv = document.createElement('div');
        listRowTds[i].childNodes.forEach(function(item){
            rowDiv.appendChild(item.cloneNode(true));
        });
        values.push(rowDiv);
    }

    const tableBody = document.createElement('tbody');
    for (let i = 0; i < keys.length; i++) {
        if (!values[i]) return;
        tableBody.appendChild(
            createTableRow([
                createTableCell(keys[i]),
                createTableCell(values[i])
            ])
        );
    }

    tableEl.appendChild(tableBody);
}

function removeContentTable(contentEl) {
    if (!contentEl) return;

    const tableEl = contentEl.querySelector('table');
    if (tableEl) {
        tableEl.remove();
    }
}

function createTableCell(innerText, classes) {
    const tableCell = document.createElement('td');
    if (classes)
        tableCell.classList.add(classes);

    if (innerText instanceof Element) {
        tableCell.appendChild(innerText);
    } else {
        tableCell.innerText = innerText;
    }

    return tableCell;
}

function createTableRow(cells, classes) {
    const tableRow = document.createElement('tr');
    if (classes)
        tableRow.classList.add(classes);
    for (let i = 0; i < cells.length; i++) {
        tableRow.appendChild(cells[i]);
    }
    return tableRow;
}

function createTableHead(tableRow) {
    const tableHead = document.createElement('thead');
    tableHead.appendChild(tableRow);
    return tableHead;
}